import { useEffect, useMemo, useState } from 'react';

import { InfoOutlined } from '@mui/icons-material';
import { FormControl, FormControlLabel, Stack, Switch, Tooltip, Typography } from '@mui/material';

import DisabledTooltipContent from 'domains/campaigns/components/CustomCampaign/components/DisabledTooltipContent';
import { clearExitCriteria, setExitCriteria } from 'domains/campaigns/components/CustomCampaign/customCampaignActions';
import { ExitCriteriaFields, Send } from 'domains/campaigns/types';
import Button from 'domains/core/components/Button';
import SegmentDetailsDisplay from 'domains/segments/pages/SegmentsPage/SegmentsTable/SegmentDetails/SegmentDetailsDisplay';
import { SegmentDefinition } from 'domains/segments/types';
import useSegmentPreviewCount from 'hooks/mutations/useSegmentPreviewCount';
import useSegments from 'hooks/queries/useSegments';
import { getNumberWithCommas } from 'utils';

import ExitCriteriaDialog from './ExitCriteriaDialog';
import { buildScopedExitCountDefinition } from './helpers';

const tooltipMessage =
    'Contacts who match this audience after entering the campaign exit it and receive no further sends. Exited contacts never re-enter.';

const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
    flexDirection: 'row',
};

const infoIconStyle = {
    fontSize: '14px',
    marginTop: '4px',
    marginLeft: '4px',
};

export type Props = {
    dispatchCampaignAction: React.Dispatch<any>;
    exitCriteria?: ExitCriteriaFields | null;
    setIsContinueEnabled: (isContinueEnabled: boolean) => void;
    disabled?: boolean;
    /**
     * The campaign's own sends, surfaced in the exit-criteria builder's engagement send pickers so a
     * rule can reference this campaign's sends.
     */
    campaignSends?: Send[];
    /**
     * The campaign's starting-audience definition. When provided, the matching-contact count is scoped
     * to the starting audience (entry ∩ exit); when omitted/null the count is hidden (cohort / A-B /
     * continual entry audiences, where a meaningful scoped count isn't available).
     */
    startingAudienceDefinition?: SegmentDefinition | null;
};

/**
 * Inline exit-criteria control used in the CustomCampaign builder's Schedule step: a toggle that opens
 * the authoring dialog and a summary of the committed criteria. The live details page does not use this
 * component — it edits exit criteria through ExitCriteriaDialog directly (in a single modal with an
 * in-modal toggle and a Save confirmation).
 */
const CampaignExitCriteria = ({
    dispatchCampaignAction,
    exitCriteria,
    setIsContinueEnabled,
    disabled = false,
    campaignSends,
    startingAudienceDefinition,
}: Props) => {
    const [checked, setChecked] = useState(Boolean(exitCriteria));
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const { data: segments, isInitialLoading: isSegmentsLoading } = useSegments({ enabled: checked });

    const {
        mutate: previewCount,
        data: countData,
        isLoading: isCountLoading,
        isError: isCountError,
    } = useSegmentPreviewCount();

    // Recompute the starting-audience-scoped matching count whenever the committed definition changes.
    // Hidden when no starting-audience definition is available (cohort / A-B / continual).
    const canScopeCount = startingAudienceDefinition != null;
    useEffect(() => {
        if (exitCriteria && canScopeCount) {
            previewCount(buildScopedExitCountDefinition(startingAudienceDefinition, exitCriteria));
        }
    }, [exitCriteria, canScopeCount, startingAudienceDefinition, previewCount]);

    useEffect(() => {
        // Optional feature, but if enabled a committed definition must exist before continuing.
        setIsContinueEnabled(checked ? Boolean(exitCriteria) : true);
    }, [checked, exitCriteria, setIsContinueEnabled]);

    const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setChecked(true);
            setIsDialogOpen(true);
        } else {
            setChecked(false);
            setIsDialogOpen(false);
            dispatchCampaignAction(clearExitCriteria());
        }
    };

    const handleSave = (definition: ExitCriteriaFields | null) => {
        if (definition) {
            dispatchCampaignAction(setExitCriteria(definition));
        } else {
            dispatchCampaignAction(clearExitCriteria());
        }
        setIsDialogOpen(false);
    };

    const handleCancel = () => {
        setIsDialogOpen(false);
        // If the toggle was just switched on but nothing was committed, revert it.
        if (!exitCriteria) setChecked(false);
    };

    const savedAudienceName = useMemo(() => {
        if (!exitCriteria?.segmentId || !segments) return null;
        return segments.find((segment) => segment.id === exitCriteria.segmentId)?.name ?? null;
    }, [exitCriteria, segments]);

    const matchCount = countData?.count;

    return (
        <DisabledTooltipContent disabled={disabled}>
            <FormControl sx={disabled ? { opacity: 0.6 } : {}}>
                <Stack sx={headerStyle}>
                    <Typography variant="body2">Campaign Exit Criteria</Typography>
                    <Tooltip title={tooltipMessage}>
                        <InfoOutlined sx={infoIconStyle} aria-label="Exit criteria info" />
                    </Tooltip>
                </Stack>
                <FormControlLabel
                    control={
                        <Switch
                            color="secondary"
                            size="small"
                            checked={checked}
                            onChange={handleToggle}
                            sx={{ marginLeft: '6px' }}
                            disabled={disabled}
                            inputProps={{ 'aria-label': 'Enable campaign exit criteria' }}
                        />
                    }
                    label={<Typography variant="body2">Contacts matching an audience exit this Curation</Typography>}
                    disabled={disabled}
                />
                {checked && exitCriteria && (
                    <Stack marginTop="8px" spacing={1} data-testid="exit-criteria-summary">
                        {savedAudienceName && (
                            <Typography variant="body2">
                                From audience: <strong>{savedAudienceName}</strong>
                            </Typography>
                        )}
                        <SegmentDetailsDisplay segmentDefinition={exitCriteria} />
                        {canScopeCount && matchCount != null && !isCountLoading && !isCountError && (
                            <Typography variant="caption" color="text.secondary" data-testid="exit-criteria-count">
                                ~{getNumberWithCommas(Number(matchCount))} of your starting audience match this exit
                                criteria.
                            </Typography>
                        )}
                        <Stack direction="row">
                            <Button
                                variant="text"
                                color="secondary"
                                onClick={() => setIsDialogOpen(true)}
                                disabled={disabled}
                                data-testid="edit-exit-criteria"
                            >
                                Edit
                            </Button>
                        </Stack>
                    </Stack>
                )}
                {isDialogOpen && (
                    <ExitCriteriaDialog
                        isOpen={isDialogOpen}
                        initialCriteria={exitCriteria}
                        segments={segments}
                        isSegmentsLoading={isSegmentsLoading}
                        campaignSends={campaignSends}
                        startingAudienceDefinition={startingAudienceDefinition}
                        onClose={handleCancel}
                        onSave={handleSave}
                    />
                )}
            </FormControl>
        </DisabledTooltipContent>
    );
};

export default CampaignExitCriteria;
